// Finance (MUI) — /finance. Three tabs:
//   Review      — sizing workbench: server + backup size, editable Data Tier
//   Monitor     — daily/monthly fleet spend, movers, margin
//   Attribution — per-resource ownership + assign (folded in here rather than a
//                 separate page, so the finance surface is one place)
// Costs shown are LOADED (direct + the customer's share of SHARED); the split is
// printed under the figure so the direct number stays inspectable.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box, Button, CircularProgress, MenuItem, Tab, Tabs, TextField,
} from "@mui/material";
import {
  getFinanceOverview, startFinanceRefresh, getFinanceStatus, saveFinanceSettings,
  getFinanceAttribution, getFinanceInventory, assignFinanceResource,
  type FinanceMonthCost, type FinanceMonthUsage, type FinanceOverview, type FinanceRun,
  type CustomerFinanceSettings, type FinanceAttribution, type FinanceInventory, type FinanceResource,
} from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { SOURCE_LABELS, SOURCE_HINTS, SOURCE_ORDER, sourceTone, shortResourceId } from "./finance-ui";
import {
  KpiCard, SectionCard, CardLabel, AreaChart, Icon, EmptyState, StatusPill, fmtUSD, fmtNZD, fmtBytes,
} from "../../mui";

const c = brainTokens.color;

const TOTAL = "__TOTAL__";
const BUCKET_LABELS: Record<string, string> = { SHARED: "Shared multi-tenant", MOD: "Internal / devops", UNRESOLVED: "Unresolved" };
const BUCKET_HINTS: Record<string, string> = {
  SHARED: "shared RDS / Roxen / EFS hosts — allocated out to customers",
  MOD: "the brain, CI, tooling, demo sites — never allocated",
  UNRESOLVED: "no resolver layer could attribute it — needs a mapping",
};
const PLATFORM_CODES = new Set([...Object.keys(BUCKET_LABELS), TOTAL]);
const TIER_LABELS: Record<string, string> = { small: "Small", medium: "Medium", large: "Large" };
const DATA_TIERS = ["small", "medium", "large"];
/** Review-table window. A running 30 days beats a calendar month: no
 *  partial-month dip, and the previous 30 days is a fair comparison. */
const WINDOW_DAYS = 30;

type TierProvenance = "unset" | "suggested" | "confirmed";
type FilterKey = "all" | "unreviewed" | "unset" | "nosub";
type PageTab = "review" | "monitor" | "attribution";

function usd(n: number): string { return fmtUSD(n); }
function usdSigned(n: number): string {
  return `${n >= 0 ? "+" : "−"}$${Math.round(Math.abs(n)).toLocaleString("en-US")}`;
}
function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
}
function monthName(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString(undefined, { month: "long" });
}
function seriesByMonth(rows: FinanceMonthCost[]): { month: string; cost: number }[] {
  const map = new Map<string, number>();
  for (const r of rows) map.set(r.month, (map.get(r.month) ?? 0) + r.cost);
  return [...map.entries()].map(([month, cost]) => ({ month, cost })).sort((a, b) => a.month.localeCompare(b.month));
}
function splitCurrent(series: { month: string; cost: number }[]) {
  const current = series.length ? series[series.length - 1] : undefined;
  const full = series.slice(0, -1);
  return { current, lastFull: full[full.length - 1], prevFull: full[full.length - 2] };
}
function projectMonth(mtd: number, month: string): number | null {
  const now = new Date();
  const [y, m] = month.split("-").map(Number);
  if (now.getFullYear() !== y || now.getMonth() + 1 !== m) return null;
  const daysIn = new Date(y, m, 0).getDate();
  return (mtd / Math.max(1, now.getDate())) * daysIn;
}

/** Rank instance types by size suffix so the column shows the largest. */
function instanceSizeRank(type: string): number {
  const size = (type.split(".")[1] ?? "").toLowerCase();
  if (size.includes("metal") || size.includes("12xlarge") || size.includes("16xlarge") || size.includes("24xlarge")) return 90;
  if (size.includes("8xlarge")) return 80;
  if (size.includes("4xlarge")) return 70;
  if (size.includes("2xlarge")) return 60;
  if (size.includes("xlarge")) return 50;
  if (size.includes("large")) return 40;
  if (size === "medium") return 30;
  if (size === "small") return 20;
  if (size === "micro" || size === "nano") return 10;
  return 0;
}

/** Format "t3.small" → "T3 small". */
function formatInstanceType(type: string): string {
  const [family, size] = type.split(".");
  if (!size) return type;
  return `${family.toUpperCase()} ${size}`;
}

function primaryServerLabel(instanceTypes: string[]): string {
  if (!instanceTypes.length) return "Shared";
  const sorted = [...instanceTypes].sort((a, b) => instanceSizeRank(b) - instanceSizeRank(a));
  return formatInstanceType(sorted[0]);
}

function suggestedDataTier(usage?: FinanceMonthUsage): string {
  const types = usage?.instanceTypes;
  const backupBytes = usage?.backupBytes;
  const band = (() => {
    if (!types?.length) return "shared";
    const sizes = types.map((t) => (t.split(".")[1] ?? "").toLowerCase());
    if (sizes.some((s) => s.includes("large"))) return "large";
    if (sizes.some((s) => s === "medium") || types.length >= 2) return "medium";
    return "small";
  })();
  const plan = (() => {
    if (typeof backupBytes !== "number") return undefined;
    const gib = backupBytes / 1024 ** 3;
    if (gib <= 1) return "0-1";
    if (gib <= 5) return "1-5";
    if (gib <= 20) return "5-20";
    return "20+";
  })();
  const computeRank = band === "large" ? 3 : band === "medium" ? 2 : band === "small" ? 1 : 0;
  const dataRank = plan === "20+" ? 3 : plan === "5-20" ? 2 : plan === "1-5" ? 1 : plan === "0-1" ? 0 : 0;
  const avg = (computeRank + dataRank) / 2;
  if (avg <= 1) return "small";
  if (avg <= 2) return "medium";
  return "large";
}

function tierProvenance(fin: CustomerFinanceSettings): TierProvenance {
  if (!fin.dataTier) return "unset";
  if (fin.tierSource === "suggested") return "suggested";
  return "confirmed";
}

function customerFinanceHref(customerId: string): string {
  return `/customers/${encodeURIComponent(customerId)}?tab=finance`;
}

/** Baseline below which a percentage is noise rather than information. A customer
 *  that just migrated onto its own server went $0.02 → $9, which is a true
 *  +43,400% and a useless thing to print. */
const DELTA_FLOOR_USD = 2;

function Delta({ now, prev }: { now?: number; prev?: number }) {
  const muted = { fontFamily: brainTokens.font.mono, fontSize: 11, color: c.faint } as const;
  if (!now && !prev) return <Box component="span" sx={muted}>—</Box>;
  // New (or newly-attributed) customers have no meaningful baseline to divide by.
  if (!prev || prev < DELTA_FLOOR_USD) {
    return <Box component="span" sx={{ ...muted, fontWeight: 600, color: c.tealInk }}>new</Box>;
  }
  if (!now) return <Box component="span" sx={{ ...muted, fontWeight: 600, color: c.successText2 }}>gone</Box>;
  const pct = ((now - prev) / prev) * 100;
  if (Math.abs(pct) < 0.5) return <Box component="span" sx={muted}>±0%</Box>;
  return (
    <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, fontWeight: 600, color: pct > 0 ? c.dangerText3 : c.successText2 }}>
      {pct > 0 ? "+" : ""}{pct.toFixed(0)}%
    </Box>
  );
}

// --- Attribution panel (Finance › Attribution tab) ---------------------------
// Resource-level view of how every cost-bearing AWS resource got its owner, and
// the assign action for the ones nothing could claim. Assigning is retroactive:
// the next pipeline run restates all 13 months.

function AttributionPanel({ codes, onAssigned }: { codes: string[]; onAssigned: () => void }) {
  const [attr, setAttr] = useState<FinanceAttribution | null>(null);
  const [inv, setInv] = useState<FinanceInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"backlog" | "all" | "internal">("backlog");
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [a, i] = await Promise.all([
        getFinanceAttribution(),
        getFinanceInventory({ minCost: 0.01, limit: 3000 }),
      ]);
      setAttr(a); setInv(i);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const assign = useCallback(async (resourceId: string, code: string) => {
    setBusy(resourceId);
    try {
      const res = await assignFinanceResource({ resourceId, code });
      if (!res.ok) { setErr(res.error ?? "Assign failed"); return; }
      await load();
      onAssigned();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(null); }
  }, [load, onAssigned]);

  const shaped = useMemo(() => {
    if (!attr || !inv) return null;
    const bySource = SOURCE_ORDER
      .map((key) => ({ key, ...(attr.bySource[key] ?? { cost: 0, count: 0 }) }))
      .filter((x) => x.count > 0);
    const total = bySource.reduce((a, x) => a + x.cost, 0);
    const binned = (attr.bySource.unresolved?.cost ?? 0) + (attr.bySource.default?.cost ?? 0);
    const term = search.trim().toLowerCase();
    const hit = (r: FinanceResource) => !term
      || r.resourceId.toLowerCase().includes(term)
      || r.service.toLowerCase().includes(term)
      || r.code.toLowerCase().includes(term)
      || r.region.toLowerCase().includes(term);
    const backlog = inv.resources.filter((r) => r.source === "default" || r.source === "unresolved").filter(hit);
    const internal = inv.resources.filter((r) => r.code === "MOD").filter(hit);
    return {
      bySource, total, binned,
      list: view === "backlog" ? backlog : view === "internal" ? internal : inv.resources.filter(hit),
      internalCost: internal.reduce((a, r) => a + r.cost, 0),
      backlogCost: backlog.reduce((a, r) => a + r.cost, 0),
    };
  }, [attr, inv, search, view]);

  if (loading && !shaped) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: "40px" }}><CircularProgress size={20} /></Box>;
  }
  if (!shaped) {
    return <EmptyState title="No inventory yet">{err ?? "The finance pipeline has not produced an inventory yet — hit Refresh."}</EmptyState>;
  }
  const a = shaped;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      {err && (
        <Box sx={{ p: "10px 12px", borderRadius: "8px", border: `1px solid ${c.cardBorder}`, fontSize: 12.5, color: c.dangerText }}>{err}</Box>
      )}

      <SectionCard label="How cost resolves to an owner" labelIcon={<Icon name="sitemap" size={14} />}
        actions={<CardLabel>{inv?.total ?? 0} resources · {inv?.windowDays ?? 0}-day window</CardLabel>}>
        {a.bySource.map((row) => {
          const pct = a.total > 0 ? (row.cost / a.total) * 100 : 0;
          return (
            <Box key={row.key} sx={{ display: "flex", alignItems: "center", gap: "12px", py: "8px", borderBottom: `1px solid ${c.borderLight3}` }}>
              <Box sx={{ flex: "0 0 150px" }}><StatusPill tone={sourceTone(row.key)} label={SOURCE_LABELS[row.key] ?? row.key} /></Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: c.borderLight3, overflow: "hidden" }}>
                  <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: row.key === "unresolved" || row.key === "default" ? c.labelMuted : c.teal }} />
                </Box>
                <Box sx={{ mt: "4px", fontSize: 11, color: c.faint }}>{SOURCE_HINTS[row.key] ?? ""}</Box>
              </Box>
              <Box sx={{ flex: "0 0 90px", textAlign: "right", fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 600 }}>{usd(row.cost)}</Box>
              <Box sx={{ flex: "0 0 60px", textAlign: "right", fontFamily: brainTokens.font.mono, fontSize: 11, color: c.faint }}>{row.count}</Box>
            </Box>
          );
        })}
      </SectionCard>

      <Box sx={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
        {([
          ["backlog", `No real owner (${usd(a.backlogCost)})`],
          ["all", "All resources"],
          ["internal", `Internal / MOD (${usd(a.internalCost)})`],
        ] as const).map(([key, label]) => (
          <Button key={key} size="small" variant={view === key ? "contained" : "outlined"}
            onClick={() => setView(key)} sx={{ fontSize: 12, textTransform: "none" }}>{label}</Button>
        ))}
        <TextField size="small" placeholder="Filter by id, service, region or code"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, maxWidth: 380, "& .MuiInputBase-root": { fontSize: 12.5 } }} />
      </Box>

      <SectionCard
        label={view === "backlog" ? "Nothing could attribute these — binned to MOD" : view === "internal" ? "Internal / non-customer (never allocated)" : "All cost-bearing resources"}
        labelIcon={<Icon name="list" size={14} />}
        actions={<CardLabel>assigning restates all 13 months</CardLabel>}
      >
        {a.list.length === 0 ? (
          <EmptyState title="Nothing here">Every cost-bearing resource resolves to a real owner.</EmptyState>
        ) : a.list.slice(0, 300).map((r) => (
          <Box key={r.resourceId} sx={{ display: "flex", alignItems: "center", gap: "12px", py: "9px", borderBottom: `1px solid ${c.borderLight3}` }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {shortResourceId(r.resourceId)}
              </Box>
              <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, color: c.faint }}>
                {r.service.replace(/^Amazon|^AWS/, "")}{r.region ? ` · ${r.region}` : ""}{r.tagCode ? ` · tag ${r.tagCode}` : ""}
              </Box>
            </Box>
            <Box sx={{ flex: "0 0 80px", textAlign: "right", fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 600 }}>{usd(r.cost)}</Box>
            <Box sx={{ flex: "0 0 120px", fontFamily: brainTokens.font.mono, fontSize: 11, color: c.teal }}>
              {r.weights
                ? Object.entries(r.weights).map(([k, w]) => `${k} ${(w * 100).toFixed(0)}%`).join(" · ")
                : r.code}
            </Box>
            <Box sx={{ flex: "0 0 140px" }}><StatusPill tone={sourceTone(r.source)} label={SOURCE_LABELS[r.source] ?? r.source} /></Box>
            <Box sx={{ flex: "0 0 210px", display: "flex", gap: "6px", alignItems: "center" }}>
              <TextField select size="small" value={draft[r.resourceId] ?? ""}
                onChange={(e) => setDraft((p) => ({ ...p, [r.resourceId]: e.target.value }))}
                sx={{ minWidth: 132, "& .MuiInputBase-root": { fontSize: 12 } }}
                SelectProps={{ displayEmpty: true }}>
                <MenuItem value="" disabled sx={{ fontSize: 12 }}>Assign to…</MenuItem>
                {codes.map((code) => <MenuItem key={code} value={code} sx={{ fontSize: 12 }}>{code}</MenuItem>)}
              </TextField>
              <Button size="small" variant="outlined" disabled={!draft[r.resourceId] || busy === r.resourceId}
                onClick={() => void assign(r.resourceId, draft[r.resourceId])} sx={{ fontSize: 11, minWidth: 56 }}>
                {busy === r.resourceId ? "…" : "Set"}
              </Button>
            </Box>
          </Box>
        ))}
        {a.list.length > 300 && (
          <Box sx={{ pt: "10px", fontSize: 11.5, color: c.faint }}>
            Showing the 300 most expensive of {a.list.length}. Narrow the filter to see more.
          </Box>
        )}
      </SectionCard>
    </Box>
  );
}

function useRefresh(onDone: () => void) {
  const [refreshing, setRefreshing] = useState(false);
  const baseline = useRef(0);
  const start = useCallback(async (lastRun: FinanceRun | null) => {
    baseline.current = lastRun?.ts ?? 0;
    setRefreshing(true);
    try { await startFinanceRefresh(); } catch { setRefreshing(false); return; }
    const startedAt = Date.now();
    const poll = async () => {
      const run = await getFinanceStatus().catch(() => null);
      if (run && run.ts > baseline.current) { setRefreshing(false); onDone(); return; }
      if (Date.now() - startedAt > 180_000) { setRefreshing(false); return; }
      window.setTimeout(() => void poll(), 4000);
    };
    window.setTimeout(() => void poll(), 4000);
  }, [onDone]);
  return { refreshing, start };
}

function BarRow({ name, sub, value, max, prev, onClick }: {
  name: string; sub?: string; value: number; max: number; prev?: number; onClick?: () => void;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: "12px", py: "8px",
        borderBottom: `1px solid ${c.borderLight3}`, cursor: onClick ? "pointer" : "default",
        "&:hover .bar-name": onClick ? { color: c.tealInk } : {},
      }}
    >
      <Box sx={{ flex: "0 1 190px", minWidth: 100 }}>
        <Box className="bar-name" sx={{ fontSize: 13, fontWeight: 500, color: c.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</Box>
        {sub && <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, color: c.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</Box>}
      </Box>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, background: c.borderLight, overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${(value / max) * 100}%`, background: c.tealInk2, borderRadius: 3 }} />
      </Box>
      <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 600, color: c.ink, minWidth: 60, textAlign: "right" }}>{usd(value)}</Box>
      {prev !== undefined && <Box sx={{ minWidth: 40, textAlign: "right" }}><Delta now={value} prev={prev} /></Box>}
    </Box>
  );
}

function BillSplit({ split, total }: { split: { key: string; name: string; value: number; hint: string }[]; total: number }) {
  const seg: Record<string, string> = {
    customers: c.teal, shared: c.billShared, internal: c.billInternal, untagged: c.warnDot, nonattr: c.billDead,
  };
  if (!split.length || total <= 0) return null;
  return (
    <>
      <Box sx={{ display: "flex", gap: "2px", height: 14, borderRadius: 4, overflow: "hidden", mb: "14px" }}>
        {split.map((s) => <Box key={s.key} title={`${s.name} — ${usd(s.value)}`} sx={{ flexGrow: s.value, background: seg[s.key] ?? c.faint }} />)}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px 18px" }}>
        {split.map((s) => (
          <Box key={s.key} title={s.hint} sx={{ display: "inline-flex", alignItems: "center", gap: "7px", fontSize: 12.5 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: 2, background: seg[s.key] ?? c.faint }} />
            <Box component="span" sx={{ color: c.ink3 }}>{s.name}</Box>
            <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontWeight: 600, color: c.ink }}>{usd(s.value)}</Box>
            <Box component="span" sx={{ color: c.faint }}>{(s.value / total) * 100 >= 1 ? `${((s.value / total) * 100).toFixed(0)}%` : "<1%"}</Box>
          </Box>
        ))}
      </Box>
    </>
  );
}

interface FinRow {
  code: string; customerId?: string; name: string;
  cost: number; prev: number;
  /** Share of the SHARED pool for the ranking month. */
  allocated: number;
  /** cost + allocated over the rolling window — what the customer costs us. */
  loaded: number;
  prevLoaded: number;
  /** Calendar-month loaded cost, for the bill-reconciling Monitor views. */
  monthLoaded: number;
  instanceTypes: string[]; backupBytes?: number; ebsBytes?: number;
  serverLabel: string;
  tier?: string;
  suggestedTier: string;
  provenance: TierProvenance;
  subscription?: number;
  reviewedAt?: string; reviewedBy?: string;
  unreviewed: boolean;
}

const GRID_COLS = "minmax(160px, 1.4fr) 96px 120px 100px 120px 40px";

function DataTierCell({
  row, editing, onEdit, onSave, onBlur,
}: {
  row: FinRow;
  editing: boolean;
  onEdit: () => void;
  onSave: (tier: string) => void;
  onBlur: () => void;
}) {
  const showSelect = editing || row.provenance === "unset";
  if (!showSelect) {
    return (
      <Box
        onClick={row.customerId ? onEdit : undefined}
        sx={{
          fontSize: 13.5, fontWeight: 600, color: c.ink, cursor: row.customerId ? "pointer" : "default",
          py: "4px", px: "2px", borderRadius: "6px",
          "&:hover": row.customerId ? { bgcolor: c.panelBg } : {},
        }}
        title={row.provenance === "suggested" ? "Suggested — click to confirm or change" : "Click to change"}
      >
        {TIER_LABELS[row.tier ?? ""] ?? row.tier}
        {row.provenance === "suggested" && (
          <Box sx={{ fontSize: 10.5, fontWeight: 500, color: c.warnText3, mt: "2px" }}>Suggested</Box>
        )}
      </Box>
    );
  }
  return (
    <TextField
      select size="small" disabled={!row.customerId} fullWidth autoFocus={editing}
      value={row.tier ?? ""}
      onChange={(e) => {
        onSave(e.target.value);
        onBlur();
      }}
      onBlur={onBlur}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px", fontSize: 13,
          ...(row.provenance === "unset"
            ? { borderColor: c.border, bgcolor: c.white }
            : {}),
        },
      }}
    >
      <MenuItem value=""><em>Unset · sugg {TIER_LABELS[row.suggestedTier]}</em></MenuItem>
      {DATA_TIERS.map((t) => <MenuItem key={t} value={t}>{TIER_LABELS[t]}</MenuItem>)}
    </TextField>
  );
}

function ReviewTable({
  rows, filter, expanded, editingTier, windowFrom, windowTo, onToggle, onEditTier, onSaveTier, onBlurTier, onConfirm,
}: {
  rows: FinRow[]; filter: FilterKey;
  windowFrom?: string; windowTo?: string;
  expanded: Set<string>; editingTier: string | null;
  onToggle: (code: string) => void;
  onEditTier: (code: string) => void;
  onSaveTier: (customerId: string, tier: string) => void;
  onBlurTier: () => void;
  onConfirm: (row: FinRow) => void;
}) {
  const filtered = rows.filter((r) => {
    if (filter === "unreviewed") return r.unreviewed;
    if (filter === "unset") return r.provenance === "unset";
    if (filter === "nosub") return r.subscription == null;
    return true;
  });

  const headSx = {
    fontSize: 10.5, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" as const,
    color: c.labelMuted, py: "10px", px: "12px", borderBottom: `1px solid ${c.cardBorder}`,
  };

  if (!filtered.length) {
    return <EmptyState icon="filter" title="No customers match this filter" />;
  }


  return (
    <Box sx={{ bgcolor: c.white, border: `1px solid ${c.cardBorder}`, borderRadius: "12px", overflow: "hidden" }}>
      {windowFrom && windowTo && (
        <Box sx={{ px: "12px", py: "8px", bgcolor: c.canvas, borderBottom: `1px solid ${c.borderLight3}`,
          fontFamily: brainTokens.font.mono, fontSize: 10.5, color: c.faint }}>
          rolling 30 days · {windowFrom} → {windowTo} · direct + allocated shared · change vs the previous 30 days
        </Box>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: GRID_COLS, bgcolor: c.canvas, borderBottom: `1px solid ${c.cardBorder}` }}>
        <Box sx={{ ...headSx }}>Customer</Box>
        <Box sx={{ ...headSx, textAlign: "right" }}>Loaded · last 30d</Box>
        <Box sx={{ ...headSx }}>Server</Box>
        <Box sx={{ ...headSx }}>Backup</Box>
        <Box sx={{ ...headSx }}>Data tier</Box>
        <Box sx={headSx} />
      </Box>
      {filtered.map((r) => {
        const open = expanded.has(r.code);
        return (
          <Box key={r.code}>
            <Box
              sx={{
                display: "grid", gridTemplateColumns: GRID_COLS, alignItems: "center", minHeight: 56,
                borderBottom: `1px solid ${c.borderLight3}`, px: 0,
                "&:hover": { bgcolor: c.panelBg3 },
              }}
            >
              <Box sx={{ px: "12px", py: "10px", minWidth: 0 }}>
                {r.customerId ? (
                  <Box component={RouterLink} to={customerFinanceHref(r.customerId)}
                    sx={{ fontSize: 14, fontWeight: 600, color: c.teal, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                    {r.name}
                  </Box>
                ) : (
                  <Box sx={{ fontSize: 14, fontWeight: 600, color: c.ink }}>{r.name}</Box>
                )}
                <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 10.5, color: c.faint }}>{r.code}</Box>
              </Box>
              <Box sx={{ px: "12px", textAlign: "right" }}>
                <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 14, fontWeight: 600 }}>{usd(r.loaded)}</Box>
                {r.allocated >= 0.5 && (
                  <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, color: c.faint }}>
                    {usd(r.cost)} + {usd(r.allocated)} shared
                  </Box>
                )}
                <Delta now={r.loaded} prev={r.prevLoaded} />
              </Box>
              <Box sx={{ px: "12px" }}>
                <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 13, fontWeight: 500, color: c.ink }}>
                  {r.serverLabel}
                </Box>
                {r.instanceTypes.length > 1 && (
                  <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 10.5, color: c.faint, mt: "2px" }}>
                    +{r.instanceTypes.length - 1} more
                  </Box>
                )}
              </Box>
              <Box sx={{ px: "12px" }}>
                <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 13, fontWeight: 500, color: r.backupBytes != null ? c.ink : c.faint }}>
                  {r.backupBytes != null ? fmtBytes(r.backupBytes) : "—"}
                </Box>
              </Box>
              <Box sx={{ px: "8px" }}>
                <DataTierCell
                  row={r}
                  editing={editingTier === r.code}
                  onEdit={() => onEditTier(r.code)}
                  onSave={(tier) => r.customerId && onSaveTier(r.customerId, tier)}
                  onBlur={onBlurTier}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button size="small" onClick={() => onToggle(r.code)} sx={{ minWidth: 26, width: 26, height: 26, p: 0, fontSize: 11, color: c.muted }}>
                  {open ? "▲" : "▼"}
                </Button>
              </Box>
            </Box>
            {open && (
              <Box sx={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px",
                bgcolor: c.canvas, borderBottom: `1px solid ${c.cardBorder}`,
                borderTop: `1px dashed ${c.border}`, p: "16px 20px",
              }}>
                <Box>
                  <Box sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: c.labelMuted, mb: 1 }}>Running instances</Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 1 }}>
                    {(r.instanceTypes.length ? r.instanceTypes : ["shared"]).map((t) => (
                      <Box key={t} sx={{ fontFamily: brainTokens.font.mono, fontSize: 11.5, bgcolor: c.cardBorder, px: "8px", py: "3px", borderRadius: "6px" }}>{t}</Box>
                    ))}
                  </Box>
                  <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11.5, color: c.faint }}>
                    Backup {fmtBytes(r.backupBytes)} · EBS {fmtBytes(r.ebsBytes)}
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: c.labelMuted, mb: 1 }}>Data tier</Box>
                  <Box sx={{ fontSize: 13, color: c.ink3, mb: 1 }}>
                    {r.tier
                      ? `${TIER_LABELS[r.tier] ?? r.tier}${r.provenance === "suggested" ? " (suggested)" : ""}`
                      : `Not set — suggestion ${TIER_LABELS[r.suggestedTier]}`}
                  </Box>
                  {(r.reviewedBy || r.reviewedAt) && (
                    <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, color: c.faint, mb: 1 }}>
                      Reviewed {r.reviewedAt ?? ""}{r.reviewedBy ? ` · ${r.reviewedBy}` : ""}
                    </Box>
                  )}
                  {r.provenance === "suggested" && r.customerId && (
                    <Button size="small" variant="contained" onClick={() => onConfirm(r)}>
                      Confirm suggested tier
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
      <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11.5, color: c.faint, px: "14px", py: "10px" }}>
        Showing {filtered.length} of {rows.length} customers · click a row chevron to expand
      </Box>
    </Box>
  );
}

export function FinancePageMui() {
  const navigate = useNavigate();
  const [data, setData] = useState<FinanceOverview | null>(null);
  const [settings, setSettings] = useState<Record<string, CustomerFinanceSettings>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [tab, setTab] = useState<PageTab>("review");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [editingTier, setEditingTier] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const overview = await getFinanceOverview();
      setData(overview);
      const seed: Record<string, CustomerFinanceSettings> = {};
      for (const info of Object.values(overview.codeMap)) seed[info.customerId] = info.finance ?? {};
      setSettings(seed);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load finance data.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const { refreshing, start } = useRefresh(() => void load());

  const save = useCallback((customerId: string, patch: Partial<CustomerFinanceSettings>, opts?: { suggested?: boolean }) => {
    setSettings((prev) => ({
      ...prev,
      [customerId]: {
        ...prev[customerId],
        ...patch,
        tierSource: opts?.suggested ? "suggested" : ("dataTier" in patch ? "confirmed" : prev[customerId]?.tierSource),
      },
    }));
    const next = { ...settings[customerId], ...patch };
    const body: Parameters<typeof saveFinanceSettings>[1] = {
      subscriptionMonthly: next.subscriptionMonthly ?? null,
      suggested: opts?.suggested,
    };
    if ("dataTier" in patch) body.dataTier = next.dataTier ?? "";
    void saveFinanceSettings(customerId, body)
      .then((saved) => setSettings((prev) => ({ ...prev, [customerId]: saved })))
      .catch(() => void load());
  }, [settings, load]);

  const shaped = useMemo(() => {
    if (!data) return null;
    const totals = seriesByMonth(data.rows.filter((r) => r.code === TOTAL));
    const { current, lastFull, prevFull } = splitCurrent(totals);
    const rankMonth = lastFull?.month ?? current?.month;
    const trailing12 = totals.slice(-13, -1).reduce((s, m) => s + m.cost, 0);

    const codes = new Map<string, FinanceMonthCost[]>();
    for (const r of data.rows) { if (r.code !== TOTAL) codes.set(r.code, [...(codes.get(r.code) ?? []), r]); }
    const costAt = (code: string, m?: string) => (codes.get(code) ?? []).filter((r) => r.month === m).reduce((s, r) => s + r.cost, 0);
    // Loaded = direct + this code's share of the SHARED pool. Both are kept so
    // the direct figure stays inspectable (design §4).
    const allocAt = (code: string, m?: string) => (codes.get(code) ?? []).filter((r) => r.month === m).reduce((s, r) => s + (r.allocated ?? 0), 0);

    // --- rolling 30-day window -------------------------------------------------
    // The Review table measures a running window rather than a calendar month:
    // a partial month makes every customer look ~15% cheaper, and comparing
    // 30-days-vs-previous-30-days is apples to apples in a way that
    // month-vs-partial-month never is. Works across month boundaries because
    // `days` is populated for EVERY month since the CUR rebuild.
    const everyDay = [...new Set(data.rows.flatMap((r) => Object.keys(r.days ?? {})))].sort();
    // Drop the newest day: CUR's most recent day is still filling in, so
    // including it would drag the window down every morning.
    const completeDayList = everyDay.slice(0, -1);
    const windowDays = completeDayList.slice(-WINDOW_DAYS);
    const prevWindowDays = completeDayList.slice(-WINDOW_DAYS * 2, -WINDOW_DAYS);
    const windowSet = new Set(windowDays);
    const prevWindowSet = new Set(prevWindowDays);

    /** Sum a code's direct + allocated cost over a set of days. */
    const overDays = (code: string, want: Set<string>): { direct: number; allocated: number } => {
      let direct = 0, allocated = 0;
      for (const r of codes.get(code) ?? []) {
        for (const [day, cost] of Object.entries(r.days ?? {})) if (want.has(day)) direct += cost;
        for (const [day, cost] of Object.entries(r.daysAllocated ?? {})) if (want.has(day)) allocated += cost;
      }
      return { direct, allocated };
    };

    const rows: FinRow[] = Object.keys(data.codeMap)
      .filter((code) => !PLATFORM_CODES.has(code))
      .map((code) => {
        const info = data.codeMap[code];
        const usage = data.usage[code];
        const fin = settings[info.customerId] ?? info.finance ?? {};
        const provenance = tierProvenance(fin);
        const unreviewed = !fin.reviewedAt || provenance === "suggested" || provenance === "unset";
        const win = overDays(code, windowSet);
        const prevWin = overDays(code, prevWindowSet);
        return {
          code, customerId: info.customerId, name: info.name,
          cost: win.direct, prev: prevWin.direct,
          allocated: win.allocated,
          loaded: win.direct + win.allocated,
          prevLoaded: prevWin.direct + prevWin.allocated,
          // Calendar-month figures kept for the Monitor tab, which has to
          // reconcile against a monthly AWS bill.
          monthLoaded: costAt(code, rankMonth) + allocAt(code, rankMonth),
          instanceTypes: usage?.instanceTypes ?? [],
          backupBytes: usage?.backupBytes, ebsBytes: usage?.ebsBytes,
          serverLabel: primaryServerLabel(usage?.instanceTypes ?? []),
          tier: fin.dataTier,
          suggestedTier: suggestedDataTier(usage),
          provenance,
          subscription: fin.subscriptionMonthly,
          reviewedAt: fin.reviewedAt, reviewedBy: fin.reviewedBy,
          unreviewed,
        };
      })
      .filter((r) => r.loaded > 0.5 || r.subscription || r.tier)
      .sort((a, b) => b.loaded - a.loaded);

    const unsetCount = rows.filter((r) => r.provenance === "unset" && r.customerId).length;
    const unreviewedCount = rows.filter((r) => r.unreviewed).length;
    const noSubCount = rows.filter((r) => r.subscription == null).length;
    const reviewBadge = unreviewedCount;

    // Monthly, deliberately: this feeds "Where the bill goes", which must add up
    // to the actual monthly AWS bill — not to a rolling window.
    const customersTotal = rows.reduce((s, r) => s + r.monthLoaded, 0);
    const movers = rows
      .filter((r) => r.prevLoaded > 0.5 || r.loaded > 0.5)
      .map((r) => ({ ...r, change: r.loaded - r.prevLoaded }))
      .filter((r) => Math.abs(r.change) >= 5)
      .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
      .slice(0, 6);

    const margins = rows
      .map((r) => (r.subscription && r.subscription > 0 ? { ...r, margin: r.subscription - r.loaded, marginPct: ((r.subscription - r.loaded) / r.subscription) * 100 } : null))
      .filter((m): m is NonNullable<typeof m> => m !== null)
      .sort((a, b) => a.marginPct - b.marginPct);
    const mrr = margins.reduce((s, m) => s + (m.subscription ?? 0), 0);

    const services = new Map<string, number>();
    for (const r of data.rows) {
      if (r.code === TOTAL || r.month !== rankMonth) continue;
      for (const [svc, cost] of Object.entries(r.byService)) services.set(svc, (services.get(svc) ?? 0) + cost);
    }
    const topServices = [...services.entries()].map(([name, value]) => ({ key: name, name: name.replace(/^Amazon |^AWS /, ""), value }))
      .filter((s) => s.value >= 1).sort((a, b) => b.value - a.value).slice(0, 8);

    const bucketAt = (code: string) => costAt(code, rankMonth);
    const totalRow = data.rows.find((r) => r.code === TOTAL && r.month === rankMonth);
    const cov = totalRow?.coverage;
    const coverage = cov && cov.resolved + cov.unresolved > 0 ? (cov.resolved / (cov.resolved + cov.unresolved)) * 100 : null;
    const unresolvedCost = cov?.unresolved ?? 0;
    const unallocated = totalRow?.unallocated ?? 0;
    const nonAttr = totalRow?.nonAttributable ?? 0;
    const billTotal = totals.find((t) => t.month === rankMonth)?.cost ?? 0;
    const projected = current ? projectMonth(current.cost, current.month) : null;

    const split = billTotal > 0 ? [
      { key: "customers", name: "Customers", value: customersTotal, hint: "attributed to a customer code" },
      { key: "shared", name: "Shared", value: bucketAt("SHARED"), hint: BUCKET_HINTS.SHARED },
      { key: "internal", name: "Internal", value: bucketAt("MOD"), hint: BUCKET_HINTS.MOD },
      { key: "unresolved", name: "Unresolved", value: bucketAt("UNRESOLVED"), hint: BUCKET_HINTS.UNRESOLVED },
      { key: "unallocated", name: "Shared, unallocated", value: unallocated, hint: "shared cost with no customer footprint that day" },
      { key: "nonattr", name: "Tax & billing", value: nonAttr, hint: "tax, Savings Plan fees & credits" },
    ].filter((s) => s.value >= 1) : [];

    const deltaPct = lastFull && prevFull && prevFull.cost > 0 ? ((lastFull.cost - prevFull.cost) / prevFull.cost) * 100 : null;

    // Daily fleet spend, now available for EVERY month rather than just the
    // current one. This is what the CUR rebuild bought.
    const dayTotals = new Map<string, number>();
    for (const r of data.rows) {
      if (r.code === TOTAL) continue;
      for (const [day, cost] of Object.entries(r.days ?? {})) {
        dayTotals.set(day, (dayTotals.get(day) ?? 0) + cost);
      }
    }
    const dailySeries = [...dayTotals.entries()]
      .map(([day, cost]) => ({ month: day, cost }))
      .sort((a, b) => a.month.localeCompare(b.month));
    const daily30 = dailySeries.slice(-30);
    // The most recent CUR day is usually partial, so compare the two complete
    // days behind it rather than flattering/alarming with a half day.
    const completeDays = daily30.slice(0, -1);
    const latestDay = completeDays[completeDays.length - 1];
    const priorDay = completeDays[completeDays.length - 2];
    const dayDelta = latestDay && priorDay ? latestDay.cost - priorDay.cost : null;
    const runRate = completeDays.length >= 7
      ? (completeDays.slice(-7).reduce((s, d) => s + d.cost, 0) / 7) * 30
      : null;

    // Day-over-day movers per customer, on loaded cost.
    const dayMovers = (() => {
      if (!latestDay || !priorDay) return [];
      const at = (code: string, day: string) => {
        const rs = codes.get(code) ?? [];
        return rs.reduce((s, r) => s + (r.days?.[day] ?? 0) + (r.daysAllocated?.[day] ?? 0), 0);
      };
      return Object.keys(data.codeMap)
        .filter((code) => !PLATFORM_CODES.has(code))
        .map((code) => ({
          code,
          customerId: data.codeMap[code].customerId,
          name: data.codeMap[code].name,
          change: at(code, latestDay.month) - at(code, priorDay.month),
        }))
        .filter((m) => Math.abs(m.change) >= 0.25)
        .sort((a, b) => Math.abs(b.change) - Math.abs(a.change))
        .slice(0, 6);
    })();

    return {
      totals, current, lastFull, prevFull, rankMonth, trailing12, rows, unsetCount, unreviewedCount, noSubCount,
      reviewBadge, movers, margins, mrr, topServices, coverage, projected, split, billTotal, deltaPct,
      unresolvedCost, unallocated, daily30, latestDay, dayDelta, runRate, dayMovers,
      windowFrom: windowDays[0], windowTo: windowDays[windowDays.length - 1], windowLen: windowDays.length,
      assignableCodes: [
        ...Object.keys(data.codeMap).filter((code) => !PLATFORM_CODES.has(code)).sort(),
        "SHARED", "MOD",
      ],
    };
  }, [data, settings]);

  const applyAll = useCallback(async () => {
    if (!shaped) return;
    const targets = shaped.rows.filter((r) => r.customerId && r.provenance === "unset");
    setApplying(true);
    try {
      for (const r of targets) {
        const patch = { dataTier: r.suggestedTier };
        setSettings((prev) => ({ ...prev, [r.customerId!]: { ...prev[r.customerId!], ...patch, tierSource: "suggested" } }));
        // eslint-disable-next-line no-await-in-loop
        const saved = await saveFinanceSettings(r.customerId!, {
          dataTier: r.suggestedTier,
          subscriptionMonthly: r.subscription ?? null,
          suggested: true,
        });
        setSettings((prev) => ({ ...prev, [r.customerId!]: saved }));
      }
    } finally {
      setApplying(false);
    }
  }, [shaped]);

  const confirmSuggested = useCallback((row: FinRow) => {
    if (!row.customerId || !row.tier) return;
    save(row.customerId, { dataTier: row.tier });
  }, [save]);

  const toggleExpand = (code: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const page = (body: React.ReactNode) => (
    <Box sx={{ fontFamily: brainTokens.font.sans, color: c.ink, width: "100%", maxWidth: 1440, p: "32px 40px 80px 0", minHeight: "100%" }}>{body}</Box>
  );

  if (loading) return page(<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>);
  if (error) return page(
    <Box sx={{ textAlign: "center", py: 8, color: c.muted }}>
      <Icon name="lock" size={28} />
      <Box sx={{ mt: 1, fontSize: 15, fontWeight: 600, color: c.ink }}>Finance is restricted</Box>
      <Box sx={{ mt: 0.5, fontSize: 13 }}>{error.includes("403") || /finance|admin/i.test(error) ? "Visible to admins and the finance department only." : error}</Box>
    </Box>,
  );
  if (!data || !shaped) return page(null);

  const s = shaped;
  const chips: { key: FilterKey; label: string; count: number }[] = [
    { key: "all", label: "All", count: s.rows.length },
    { key: "unreviewed", label: "Unreviewed", count: s.unreviewedCount },
    { key: "unset", label: "Unset tier", count: s.unsetCount },
  ];

  return page(
    <>
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: "22px" }}>
        <Box>
          <Box sx={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.4px" }}>Finance</Box>
          <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, color: c.muted, mt: "4px" }}>
            AWS spend by customer account code{data.lastRun ? ` · snapshot ${new Date(data.lastRun.ts).toLocaleString()}` : ""}
          </Box>
        </Box>
        {refreshing
          ? <Box sx={{ fontSize: 13, color: c.tealInk }}>Refreshing from Cost Explorer…</Box>
          : <Button variant="outlined" startIcon={<Icon name="refresh" size={15} />} onClick={() => void start(data.lastRun)}>Refresh</Button>}
      </Box>

      {data.rows.length === 0 ? (
        <SectionCard><Box sx={{ textAlign: "center", py: 6, color: c.muted }}>No cost snapshot yet — hit Refresh to pull the first 13 months from Cost Explorer.</Box></SectionCard>
      ) : (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "16px", mb: "16px", "@media (max-width: 1100px)": { gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" } }}>
            <KpiCard label={s.lastFull ? `${monthName(s.lastFull.month)} — last full` : "Last month"} icon={<Icon name="report-money" size={13} />}
              value={s.lastFull ? usd(s.lastFull.cost) : "—"} hint={s.prevFull ? `${monthName(s.prevFull.month)}: ${usd(s.prevFull.cost)}` : undefined} />
            <KpiCard label="Month over month" icon={<Icon name={s.deltaPct != null && s.deltaPct > 0 ? "trending-up" : "trending-down"} size={13} />}
              value={s.deltaPct == null ? "—" : `${s.deltaPct > 0 ? "+" : ""}${s.deltaPct.toFixed(1)}%`}
              valueColor={s.deltaPct != null && s.deltaPct > 0 ? c.dangerText : c.successText}
              hint={s.lastFull && s.prevFull ? usdSigned(s.lastFull.cost - s.prevFull.cost) : undefined} />
            <KpiCard label={s.current ? `${monthName(s.current.month)} so far` : "This month"} icon={<Icon name="calendar" size={13} />}
              value={s.current ? usd(s.current.cost) : "—"} hint={s.projected ? `on track for ~${usd(s.projected)}` : undefined} />
            <KpiCard label="Trailing 12 months" icon={<Icon name="sum" size={13} />} value={usd(s.trailing12)} hint={`avg ${usd(s.trailing12 / 12)}/mo`} />
            <KpiCard label="Tagged coverage" icon={<Icon name="tag" size={13} />} value={s.coverage != null ? `${s.coverage.toFixed(0)}%` : "—"} hint={`${s.rows.length} customers attributed`} />
          </Box>

          <Tabs
            value={tab}
            onChange={(_, v: PageTab) => setTab(v)}
            sx={{ mb: "18px", minHeight: 40, "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14, minHeight: 40 } }}
          >
            <Tab
              value="review"
              label={
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  Review
                  {s.reviewBadge > 0 && (
                    <Box sx={{ fontSize: 11, fontWeight: 700, bgcolor: c.teal, color: c.white, borderRadius: "10px", px: "7px", py: "1px" }}>{s.reviewBadge}</Box>
                  )}
                </Box>
              }
            />
            <Tab value="monitor" label="Monitor" />
            <Tab
              value="attribution"
              label={
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  Attribution
                  {s.unresolvedCost >= 1 && (
                    <Box sx={{ fontSize: 11, fontWeight: 700, bgcolor: c.labelMuted, color: c.white, borderRadius: "10px", px: "7px", py: "1px" }}>
                      {usd(s.unresolvedCost)}
                    </Box>
                  )}
                </Box>
              }
            />
          </Tabs>

          {tab === "review" && (
            <Box sx={{
              display: "grid", gridTemplateColumns: "minmax(0, 1fr) 300px", gap: "24px", alignItems: "start",
              "@media (max-width: 1200px)": { gridTemplateColumns: "1fr" },
            }}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px", mb: "14px" }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {chips.map((ch) => {
                      const active = filter === ch.key;
                      return (
                        <Button
                          key={ch.key}
                          size="small"
                          onClick={() => setFilter(ch.key)}
                          sx={{
                            borderRadius: "20px", textTransform: "none", fontWeight: 600, fontSize: 12.5, px: "12px",
                            bgcolor: active ? c.teal : c.white, color: active ? c.white : c.ink3,
                            border: `1px solid ${active ? c.teal : c.cardBorder}`,
                            "&:hover": { bgcolor: active ? c.tealHover : c.panelBg },
                          }}
                        >
                          {ch.label} · {ch.count}
                        </Button>
                      );
                    })}
                  </Box>
                  <Button
                    size="small" variant="contained" disabled={applying || s.unsetCount === 0}
                    startIcon={applying ? <CircularProgress size={13} color="inherit" /> : <Icon name="wand" size={14} />}
                    onClick={() => void applyAll()}
                  >
                    {applying ? "Applying…" : s.unsetCount ? `Apply ${s.unsetCount} suggested tier${s.unsetCount === 1 ? "" : "s"}` : "All tiered"}
                  </Button>
                </Box>

                <ReviewTable
                  rows={s.rows} filter={filter} expanded={expanded} editingTier={editingTier}
                  windowFrom={s.windowFrom} windowTo={s.windowTo}
                  onToggle={toggleExpand}
                  onEditTier={setEditingTier}
                  onSaveTier={(customerId, tier) => save(customerId, { dataTier: tier })}
                  onBlurTier={() => setEditingTier(null)}
                  onConfirm={confirmSuggested}
                />
              </Box>

              <Box sx={{ position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: "16px" }}>
                <SectionCard label="Needs attention" labelIcon={<Icon name="alert-triangle" size={14} />}>
                  {([
                    { key: "unreviewed" as FilterKey, label: "Unreviewed", count: s.unreviewedCount },
                    { key: "unset" as FilterKey, label: "Unset tiers", count: s.unsetCount },
                    { key: "nosub" as FilterKey, label: "No subscription", count: s.noSubCount },
                  ]).map((item) => (
                    <Box
                      key={item.label}
                      onClick={() => setFilter(item.key)}
                      sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", py: "8px",
                        borderBottom: `1px solid ${c.borderLight3}`, cursor: "pointer",
                        "&:hover": { color: c.tealInk },
                      }}
                    >
                      <Box sx={{ fontSize: 13, fontWeight: 500 }}>{item.label}</Box>
                      <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 13, fontWeight: 700 }}>{item.count}</Box>
                    </Box>
                  ))}
                </SectionCard>
                {s.movers.length > 0 && (
                  <SectionCard label="Biggest movers · $ vs prev" labelIcon={<Icon name="arrows-up-down" size={14} />}>
                    {s.movers.map((m) => (
                      <Box key={m.code} onClick={() => m.customerId && navigate(customerFinanceHref(m.customerId))}
                        sx={{ display: "flex", alignItems: "center", gap: "10px", py: "8px", borderBottom: `1px solid ${c.borderLight3}`, cursor: "pointer" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ fontSize: 13, fontWeight: 500, color: c.teal }}>{m.name}</Box>
                          <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, color: c.faint }}>{usd(m.prev)} → {usd(m.cost)}</Box>
                        </Box>
                        <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 600, color: m.change > 0 ? c.dangerText3 : c.successText2 }}>{usdSigned(m.change)}</Box>
                      </Box>
                    ))}
                  </SectionCard>
                )}
              </Box>
            </Box>
          )}

          {tab === "attribution" && (
            <AttributionPanel
              codes={s.assignableCodes}
              onAssigned={() => void load()}
            />
          )}

          {tab === "monitor" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
              {s.unresolvedCost >= 1 && (
                <SectionCard label="Attribution" labelIcon={<Icon name="sitemap" size={14} />}
                  actions={
                    <Button size="small" variant="outlined" onClick={() => setTab("attribution")} sx={{ fontSize: 11.5 }}>
                      Open attribution
                    </Button>
                  }>
                  <Box sx={{ fontSize: 13, color: c.ink }}>
                    {usd(s.unresolvedCost)} of {monthLabel(s.rankMonth ?? "")} spend could not be attributed to an owner.
                  </Box>
                  <Box sx={{ mt: "4px", fontSize: 11.5, color: c.faint }}>
                    Mapping a resource is retroactive — it restates every month, not just this one.
                    {s.unallocated >= 1 ? ` A further ${usd(s.unallocated)} of shared cost had no customer footprint to land on.` : ""}
                  </Box>
                </SectionCard>
              )}
              {s.split.length > 0 && (
                <SectionCard label="Where the bill goes" labelIcon={<Icon name="chart-pie" size={14} />}
                  actions={s.rankMonth ? <CardLabel>{monthLabel(s.rankMonth)} · {usd(s.billTotal)}</CardLabel> : undefined}>
                  <BillSplit split={s.split} total={s.billTotal} />
                </SectionCard>
              )}
              {s.daily30.length > 1 && (
                <SectionCard label="Fleet spend · daily" labelIcon={<Icon name="trending-up" size={14} />}
                  actions={
                    <CardLabel>
                      {s.latestDay ? `${s.latestDay.month} · ${usd(s.latestDay.cost)}` : ""}
                      {s.runRate ? ` · 7-day run rate ${usd(s.runRate)}/mo` : ""}
                    </CardLabel>
                  }>
                  <AreaChart values={s.daily30.map((d) => d.cost)}
                    leftLabel={s.daily30[0]?.month}
                    rightLabel={s.daily30.at(-1)?.month ? `${s.daily30.at(-1)!.month} (partial)` : undefined} />
                  {s.dayDelta !== null && (
                    <Box sx={{ mt: "10px", fontFamily: brainTokens.font.mono, fontSize: 11, color: c.faint }}>
                      Last complete day {usdSigned(s.dayDelta)} vs the day before
                    </Box>
                  )}
                </SectionCard>
              )}
              <SectionCard label="Fleet spend · monthly" labelIcon={<Icon name="trending-up" size={14} />}>
                <AreaChart values={s.totals.map((t) => t.cost)}
                  leftLabel={s.totals[0] ? monthLabel(s.totals[0].month) : undefined}
                  rightLabel={s.totals.at(-1) ? `${monthLabel(s.totals.at(-1)!.month)} (so far) · ${usd(s.totals.at(-1)!.cost)}` : undefined} />
              </SectionCard>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", "@media (max-width: 1100px)": { gridTemplateColumns: "1fr" } }}>
                {s.margins.length > 0 && (
                  <SectionCard label="Margin · worst first" labelIcon={<Icon name="scale" size={14} />}
                    actions={<CardLabel>pre-FX · MRR {usd(s.mrr)}</CardLabel>}>
                    {s.margins.slice(0, 8).map((m) => (
                      <Box key={m.code} onClick={() => m.customerId && navigate(customerFinanceHref(m.customerId))}
                        sx={{ display: "flex", alignItems: "center", gap: "10px", py: "8px", borderBottom: `1px solid ${c.borderLight3}`, cursor: "pointer" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ fontSize: 13, fontWeight: 500, color: c.teal }}>{m.name}</Box>
                          <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, color: c.faint }}>{fmtNZD(m.subscription ?? 0)} sub − {usd(m.loaded)} AWS loaded{m.allocated >= 0.5 ? ` (${usd(m.cost)} + ${usd(m.allocated)} shared)` : ""}</Box>
                        </Box>
                        <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 600, color: m.margin < 0 ? c.dangerText : c.ink }}>{usdSigned(m.margin)}</Box>
                      </Box>
                    ))}
                  </SectionCard>
                )}
                {s.dayMovers.length > 0 && (
                  <SectionCard label="Day-over-day movers" labelIcon={<Icon name="arrows-up-down" size={14} />}
                    actions={<CardLabel>loaded · last complete day</CardLabel>}>
                    {s.dayMovers.map((m) => (
                      <Box key={m.code} onClick={() => m.customerId && navigate(customerFinanceHref(m.customerId))}
                        sx={{ display: "flex", alignItems: "center", gap: "10px", py: "8px", borderBottom: `1px solid ${c.borderLight3}`, cursor: "pointer" }}>
                        <Box sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: c.teal }}>{m.name}</Box>
                        <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 600, color: m.change > 0 ? c.dangerText : c.successText2 }}>
                          {usdSigned(m.change)}
                        </Box>
                      </Box>
                    ))}
                  </SectionCard>
                )}
                {s.movers.length > 0 && (
                  <SectionCard label="Biggest movers · monthly" labelIcon={<Icon name="arrows-up-down" size={14} />}>
                    {s.movers.map((m) => (
                      <Box key={m.code} onClick={() => m.customerId && navigate(customerFinanceHref(m.customerId))}
                        sx={{ display: "flex", alignItems: "center", gap: "10px", py: "8px", borderBottom: `1px solid ${c.borderLight3}`, cursor: "pointer" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ fontSize: 13, fontWeight: 500, color: c.teal }}>{m.name}</Box>
                          <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, color: c.faint }}>{usd(m.prev)} → {usd(m.cost)}</Box>
                        </Box>
                        <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12.5, fontWeight: 600, color: m.change > 0 ? c.dangerText3 : c.successText2 }}>{usdSigned(m.change)}</Box>
                      </Box>
                    ))}
                  </SectionCard>
                )}
                <SectionCard label="Fleet by service" labelIcon={<Icon name="stack-2" size={14} />}>
                  {s.topServices.length === 0 ? <Box sx={{ fontSize: 13, color: c.muted }}>No breakdown yet.</Box> : (() => {
                    const max = Math.max(1, ...s.topServices.map((t) => t.value));
                    return s.topServices.map((t) => <BarRow key={t.key} name={t.name} value={t.value} max={max} />);
                  })()}
                </SectionCard>
              </Box>
            </Box>
          )}
        </>
      )}
    </>,
  );
}
